import { useRef, useEffect, useState, useMemo } from 'react'
import type { Quapp } from '../../../types/quapp'
import type { BridgeManager } from '../../../bridges'
import { QuappSplash } from './QuappSplash'

interface QuappIframeProps {
  quapp: Quapp
  bridgeManager?: BridgeManager | null
}

/**
 * The actual iframe that loads the Quapp
 * Handles communication with the Quapp via postMessage
 */
export function QuappIframe({ quapp, bridgeManager }: QuappIframeProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  
  // Add cache-busting parameter to force fresh load
  // This prevents iframe from loading stale cached content
  const iframeSrc = useMemo(() => {
    const url = new URL(quapp.quapp_url)
    url.searchParams.set('_qf', Date.now().toString(36)) // Quapp Frame cache bust
    return url.toString()
  }, [quapp.quapp_url])

  // Handle iframe load events
  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe) return

    const handleLoad = () => {
      setIsLoading(false)
      
      // Signal to the Quapp that it's running in Web Frame
      try {
        const iframeWindow = iframe.contentWindow
        if (iframeWindow && bridgeManager) {
          // Use BridgeManager to signal ready with capabilities
          bridgeManager.signalReady(iframeWindow)
        } else if (iframeWindow) {
          // Fallback if no bridge manager
          iframeWindow.postMessage({
            type: 'QUAPP_WEB_FRAME_READY',
            version: '1.0.0',
          }, '*')
        }
      } catch (e) {
        // Cross-origin restrictions may prevent this
        console.debug('Could not send ready message to iframe:', e)
      }
    }

    const handleError = () => {
      setIsLoading(false)
      setLoadError(true)
    }

    iframe.addEventListener('load', handleLoad)
    iframe.addEventListener('error', handleError)

    return () => {
      iframe.removeEventListener('load', handleLoad)
      iframe.removeEventListener('error', handleError)
    }
  }, [bridgeManager])

  // Set iframe origin for BridgeManager validation
  useEffect(() => {
    if (bridgeManager && quapp.quapp_url) {
      try {
        const origin = new URL(quapp.quapp_url).origin
        bridgeManager.setIframeOrigin(origin)
      } catch {
        // Invalid URL
      }
    }
  }, [bridgeManager, quapp.quapp_url])

  if (loadError) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-slate-50">
        <div className="text-center p-8 max-w-sm">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-slate-900 mb-2">
            Failed to Load
          </h2>
          <p className="text-slate-500 text-sm mb-6">
            This Quapp couldn't be loaded. It might be temporarily unavailable.
          </p>
          <button 
            onClick={() => {
              setLoadError(false)
              setIsLoading(true)
              if (iframeRef.current) {
                // Add new cache bust parameter for retry
                const url = new URL(quapp.quapp_url)
                url.searchParams.set('_qf', Date.now().toString(36))
                iframeRef.current.src = url.toString()
              }
            }}
            className="px-6 py-2 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Loading overlay — branded splash (Quapp icon on white + subtle powered-by) */}
      {isLoading && (
        <div className="absolute inset-0 z-10">
          <QuappSplash iconSrc={quapp.icon_url || undefined} />
        </div>
      )}

      {/* Quapp iframe.
          - sandbox: allow-popups-to-escape-sandbox lets OAuth/sign-in popups open
            as real top-level windows; allow-storage-access-by-user-activation lets
            the Quapp use the Storage Access API for its cookies.
          - allow: microphone + autoplay power voice features (e.g. ElevenLabs voice
            calls); identity-credentials-get enables Google FedCM sign-in in-frame. */}
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        title={quapp.display_name}
        className="absolute inset-0 w-full h-full border-0"
        allowFullScreen
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals allow-downloads allow-storage-access-by-user-activation"
        allow="camera; microphone; autoplay; clipboard-read; clipboard-write; geolocation; identity-credentials-get; encrypted-media; fullscreen"
      />
    </>
  )
}
